import { fetchMerchants as fetchMerchantsReq } from '../services/newCoreApi.js';
import { matchMerchant } from '../services/referenceMaps.js';
import {
    REQUEST_MERCHANT,
    RECEIVE_MERCHANT,
    RECEIVE_MERCHANT_ERROR,
    RESET_MERCHANT,
} from './discountServiceTypes.js';

/**
 * Resolves the portal's selected organization to a cloud-core-api merchant, once per org.
 *
 * Needed because the new services key merchants by **urlname** in the path, while the portal
 * carries `user.group = {Id, Name}` where `Name` is a display name. Both `name` and `urlname`
 * are stored, but every cds and `/data/*` call must use `urlname`.
 */

export {
    REQUEST_MERCHANT,
    RECEIVE_MERCHANT,
    RECEIVE_MERCHANT_ERROR,
    RESET_MERCHANT,
} from './discountServiceTypes.js';

function requestMerchant() {
    return { type: REQUEST_MERCHANT };
}

function receiveMerchant(merchant, groupId) {
    return { type: RECEIVE_MERCHANT, merchant, groupId, receivedAt: Date.now() };
}

function receiveMerchantError(error) {
    return { type: RECEIVE_MERCHANT_ERROR, error };
}

/**
 * Clear the resolved merchant. Dispatched on organization switch so the next page load
 * re-resolves instead of reusing the previous org's merchant.
 */
export function resetMerchant() {
    return { type: RESET_MERCHANT };
}

/**
 * Thunk: resolve the current organization's merchant. No-ops when already resolved for this org
 * or a request is in flight, so components can call it from an effect.
 * @returns {function}
 */
export function fetchMerchant() {
    return async (dispatch, getState) => {
        const { merchants, user } = getState();
        const group = user && user.group;
        if (!group || !group.Name) {
            return null;
        }
        if (merchants.fetching) {
            return merchants.merchant;
        }
        if (merchants.fetched && merchants.groupId === group.Id) {
            return merchants.merchant;
        }

        dispatch(requestMerchant());
        try {
            const merchant = matchMerchant(await fetchMerchantsReq(), group);
            if (!merchant) {
                throw new Error(
                    `No cloud-core-api merchant matches organization "${group.Name}" — `
                    + 'discounts cannot be loaded from the new service for this organization.'
                );
            }
            dispatch(receiveMerchant(merchant, group.Id));
            return merchant;
        } catch (error) {
            dispatch(receiveMerchantError(error));
            return null;
        }
    };
}
